import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  ConsentTemplateBodyRequest,
  ConsentTemplateResponse,
  NewConsentTemplateRequest,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { useBranchPicker } from '../scheduling/useBranchPicker';

/**
 * The clinic's consent texts (FR-504, DEC-024, ASM-003).
 *
 * The screen is shaped by the one rule that makes a consent worth anything: **a published version
 * is frozen.** There is no edit button on a published row. Amending writes the next version as a
 * draft, and publishing that draft retires the current one — which is why the list shows versions
 * rather than documents, and why a code with three versions is three rows.
 *
 * The starter pack installs four drafts in Arabic and English (ASM-003). They arrive unpublished on
 * purpose: they are legal documents with the clinic's name on them and counsel has not read them,
 * so nothing can be signed against words nobody has approved.
 */
export function ConsentsPage() {
  const { t } = useTranslation();
  const { clinicId } = useBranchPicker();
  const [drafting, setDrafting] = useState(false);
  const [editing, setEditing] = useState<ConsentTemplateResponse | null>(null);

  const templates = useApiQuery<ConsentTemplateResponse[]>(
    queryKeys.consentTemplates(clinicId ?? ''),
    `/clinics/${clinicId}/clinical/consents/templates`,
    { enabled: Boolean(clinicId) },
  );

  const installStarter = useApiMutation<unknown, void>(
    () => ({ path: `/clinics/${clinicId}/clinical/starter-pack`, options: { method: 'POST' } }),
    { invalidates: [queryKeys.consentTemplates(clinicId ?? ''), queryKeys.aftercareTemplates(clinicId ?? '')] },
  );

  const publish = useApiMutation<unknown, string>(
    (templateId) => ({
      path: `/clinics/${clinicId}/clinical/consents/templates/${templateId}/publish`,
      options: { method: 'POST' },
    }),
    { invalidates: [queryKeys.consentTemplates(clinicId ?? '')] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.consents')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.consents')}
        description={t('consents.description')}
        actions={
          <div className="table__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={() => installStarter.mutate()}
              disabled={installStarter.isPending}
            >
              {t('consents.installStarter')}
            </button>
            <button type="button" className="button button--primary" onClick={() => setDrafting(true)}>
              {t('consents.new')}
            </button>
          </div>
        }
      />

      {installStarter.error ? <ErrorState error={installStarter.error} /> : null}
      {publish.error ? <ErrorState error={publish.error} /> : null}

      <AsyncSection
        query={templates}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('consents.emptyTitle')}
        emptyDescription={t('consents.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('consents.code')}</th>
                  <th scope="col">{t('consents.kind')}</th>
                  <th scope="col">{t('consents.version')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((template) => (
                  <tr key={template.id}>
                    <th scope="row">{template.titleEn ?? template.code}</th>
                    <td>{t(`consents.kinds.${template.kind ?? 'GENERAL'}`)}</td>
                    <td className="ltr">{template.version}</td>
                    <td>{t(`consents.statuses.${template.status ?? 'DRAFT'}`)}</td>
                    <td>
                      <div className="table__actions">
                        {template.status === 'DRAFT' ? (
                          <>
                            <button
                              type="button"
                              className="button button--compact"
                              onClick={() => setEditing(template)}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              type="button"
                              className="button button--primary button--compact"
                              onClick={() => publish.mutate(template.id!)}
                            >
                              {t('consents.publish')}
                            </button>
                          </>
                        ) : template.status === 'PUBLISHED' ? (
                          <button
                            type="button"
                            className="button button--compact"
                            onClick={() => setEditing(template)}
                          >
                            {t('consents.amend')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      <p className="field__hint">{t('consents.frozenHint')}</p>

      {drafting ? (
        <NewTemplate clinicId={clinicId} onClose={() => setDrafting(false)} />
      ) : null}
      {editing ? (
        <EditTemplate clinicId={clinicId} template={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function NewTemplate({ clinicId, onClose }: { readonly clinicId: string; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<NonNullable<ConsentTemplateResponse['kind']>>('TREATMENT');
  const [body, setBody] = useState<ConsentTemplateBodyRequest>({
    titleEn: '',
    titleAr: '',
    bodyEn: '',
    bodyAr: '',
  });

  const draft = useApiMutation<unknown, NewConsentTemplateRequest>(
    (payload) => ({
      path: `/clinics/${clinicId}/clinical/consents/templates`,
      options: { method: 'POST', body: payload },
    }),
    { invalidates: [queryKeys.consentTemplates(clinicId)], onSuccess: onClose },
  );

  return (
    <TemplateDialog
      title={t('consents.new')}
      body={body}
      onBody={setBody}
      error={draft.error}
      onClose={onClose}
      pending={draft.isPending}
      onSave={() => draft.mutate({ code, kind, body })}
      extra={
        <div className="form-grid">
          <label className="field">
            <span className="field__label">{t('consents.code')}</span>
            <input
              className="field__input ltr"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="laser-treatment"
              required
            />
          </label>
          <label className="field">
            <span className="field__label">{t('consents.kind')}</span>
            <select
              className="field__input"
              value={kind}
              onChange={(event) =>
                setKind(event.target.value as NonNullable<ConsentTemplateResponse['kind']>)
              }
            >
              {(['TREATMENT', 'PRIVACY', 'PHOTO_CLINICAL', 'PHOTO_MARKETING', 'GENERAL'] as const).map(
                (option) => (
                  <option key={option} value={option}>
                    {t(`consents.kinds.${option}`)}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>
      }
    />
  );
}

function EditTemplate({
  clinicId,
  template,
  onClose,
}: {
  readonly clinicId: string;
  readonly template: ConsentTemplateResponse;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState<ConsentTemplateBodyRequest>({
    titleEn: template.titleEn ?? '',
    titleAr: template.titleAr ?? '',
    bodyEn: template.bodyEn ?? '',
    bodyAr: template.bodyAr ?? '',
  });

  const published = template.status === 'PUBLISHED';

  // A published version is amended — which writes the next one — and a draft is edited in place.
  // Two endpoints, because they are two different acts and only one of them is reversible.
  const save = useApiMutation<unknown, ConsentTemplateBodyRequest>(
    (payload) => ({
      path: published
        ? `/clinics/${clinicId}/clinical/consents/templates/${template.code}/amendment`
        : `/clinics/${clinicId}/clinical/consents/templates/${template.id}`,
      options: { method: published ? 'POST' : 'PUT', body: payload },
    }),
    { invalidates: [queryKeys.consentTemplates(clinicId)], onSuccess: onClose },
  );

  return (
    <TemplateDialog
      title={published ? t('consents.amend') : t('common.edit')}
      body={body}
      onBody={setBody}
      error={save.error}
      onClose={onClose}
      pending={save.isPending}
      onSave={() => save.mutate(body)}
      extra={published ? <p className="callout callout--warning">{t('consents.amendHint')}</p> : null}
    />
  );
}

function TemplateDialog({
  title,
  body,
  onBody,
  extra,
  error,
  pending,
  onSave,
  onClose,
}: {
  readonly title: string;
  readonly body: ConsentTemplateBodyRequest;
  readonly onBody: (body: ConsentTemplateBodyRequest) => void;
  readonly extra?: React.ReactNode;
  readonly error: unknown;
  readonly pending: boolean;
  readonly onSave: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal__panel">
        <header className="modal__header">
          <h2>{title}</h2>
          <button type="button" className="button button--ghost button--compact" onClick={onClose}>
            {t('common.close')}
          </button>
        </header>
        <div className="modal__body">
          {extra}
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('consents.titleAr')}</span>
              <input
                className="field__input"
                value={body.titleAr}
                onChange={(event) => onBody({ ...body, titleAr: event.target.value })}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">{t('consents.titleEn')}</span>
              <input
                className="field__input ltr"
                value={body.titleEn}
                onChange={(event) => onBody({ ...body, titleEn: event.target.value })}
                required
              />
            </label>
          </div>
          <label className="field">
            <span className="field__label">{t('consents.bodyAr')}</span>
            <textarea
              className="field__input"
              rows={10}
              value={body.bodyAr}
              onChange={(event) => onBody({ ...body, bodyAr: event.target.value })}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">{t('consents.bodyEn')}</span>
            <textarea
              className="field__input ltr"
              rows={10}
              value={body.bodyEn}
              onChange={(event) => onBody({ ...body, bodyEn: event.target.value })}
              required
            />
          </label>
          {error ? <ErrorState error={error} /> : null}
        </div>
        <footer className="modal__footer">
          <button type="button" className="button button--primary" disabled={pending} onClick={onSave}>
            {t('common.save')}
          </button>
        </footer>
      </div>
    </div>
  );
}
